import { useMemo } from 'react';
import { transliterate } from 'transliteration';

/**
 * Generate a slug suggestion from a store name.
 * Uses the `transliteration` library for Arabic/Hebrew -> Latin conversion,
 * then applies kebab-case normalization.
 *
 * @param name The store name (may be Arabic, Hebrew, English, or mixed).
 * @returns A suggested kebab-case slug, or empty string if name is empty.
 */
export function useSlugSuggestion(name: string): string {
  return useMemo(() => generateSlug(name), [name]);
}

/** Pure function for testability. Exported for unit tests. */
export function generateSlug(name: string): string {
  if (!name.trim()) return '';

  // 1. Transliterate non-Latin characters to Latin
  const latinized = transliterate(name.trim());

  // 2. Lowercase
  const lower = latinized.toLowerCase();

  // 3. Replace non-alphanumeric characters with hyphens
  const hyphenated = lower.replace(/[^a-z0-9]+/g, '-');

  // 4. Remove leading/trailing hyphens
  const trimmed = hyphenated.replace(/^-+|-+$/g, '');

  // 5. Collapse consecutive hyphens
  const collapsed = trimmed.replace(/-{2,}/g, '-');

  // 6. If empty after processing (e.g., all-symbol name), generate fallback
  if (!collapsed) {
    const suffix = Math.random().toString(36).substring(2, 8);
    return `store-${suffix}`;
  }

  // 7. Truncate to 60 chars at a word boundary
  if (collapsed.length > 60) {
    const truncated = collapsed.substring(0, 60);
    const lastHyphen = truncated.lastIndexOf('-');
    return lastHyphen > 3 ? truncated.substring(0, lastHyphen) : truncated;
  }

  return collapsed;
}
